import React, { useMemo, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { format, parse } from 'date-fns';

import InteractiveMap, {
  ESRI_SATELLITE_TILE_ATTRIBUTION,
  ESRI_SATELLITE_TILE_URL,
} from 'components/InteractiveMap';
import BrazilStatesLayer from 'components/InteractiveMap/BrazilStatesLayer';
import BrazilCountryLayer from 'components/InteractiveMap/BrazilCountryLayer';
import CanvasHeatLayer from 'components/InteractiveMap/CanvasHeatLayer';
import QueryErrorState from 'components/DesignSystem/QueryErrorState';
import Icon from 'components/DesignSystem/Icon';

import { CultureIndexAreaMap } from 'types/Meteorology/Agro/CultureIndex/Area/map';
import useMapLocationMarkers from 'hooks/Meteorology/Agro/CultureIndex/Area/useMapLocationMarkers';
import useEnrichedMarkers from 'hooks/Meteorology/Agro/CultureIndex/Area/useEnrichedMarkers';
import useMapDownload from 'hooks/Meteorology/Agro/CultureIndex/Area/useMapDownload';
import useCaption from 'hooks/Meteorology/Agro/CultureIndex/Area/useCaption';
import useHeatmapConfig from 'hooks/Meteorology/Agro/CultureIndex/Area/useHeatmapConfig';
import useHeatmapLegend from 'hooks/Meteorology/Agro/CultureIndex/Area/useHeatmapLegend';
import useAgroMapState from 'hooks/Meteorology/Agro/Maps/useAgroMapState';
import { findNearestValue } from 'helper/Meteorology/Agro/Maps/geoUtils';
import { cssVar } from 'constants/theme';

import LayersSheet from '../LayersSheet';
import DownloadSheet from '../DownloadSheet';

import './index.scss';

// No mobile não há hover: a camada "tooltip" do desktop vira o tap-tooltip sempre ativo.
const LAYER_DEFS = [
  { key: 'localidades', label: 'Localidades' },
  { key: 'limitesEstaduais', label: 'Limites estaduais' },
];

const fmt = (value?: string) => (
  value ? format(parse(value, 'yyyy-MM-dd', new Date()), 'dd/MM/yy') : ''
);

function buildMapTitle(areaMap: CultureIndexAreaMap): string {
  const cultureName = areaMap.params.culture?.label ?? areaMap.params.culture?.name ?? '';
  const stageLabel = areaMap.params.stage?.label ?? areaMap.params.stage?.name ?? '';
  const stagePart = stageLabel ? `(${stageLabel})` : '';
  const start = fmt(areaMap.params.periodStart);
  const end = fmt(areaMap.params.periodEnd);
  const period = start && end ? `${start} – ${end}` : '';
  return [cultureName, stagePart, period].filter(Boolean).join(' · ');
}

interface TapInfo {
  x: number;
  y: number;
  value: number;
}

export default function MapDisplay({
  areaMap,
  position,
  totalMaps,
  removeMap,
  removeMapDisabled,
}: {
  areaMap: CultureIndexAreaMap;
  position: number;
  totalMaps: number;
  removeMap: (map: CultureIndexAreaMap) => void;
  removeMapDisabled: boolean;
}) {
  const [layersOpen, setLayersOpen] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [tapInfo, setTapInfo] = useState<TapInfo | null>(null);

  const divRef = useRef<HTMLDivElement>(null);

  const { layers, handleToggle } = useAgroMapState(LAYER_DEFS);
  const {
    onDownloadClick, setOnDownloadClick, isDownloadDisabled,
  } = useMapDownload();
  const caption = useCaption(areaMap);
  const heatmapConfig = useHeatmapConfig();
  const legend = useHeatmapLegend(areaMap.data, heatmapConfig.gradient as Record<string, string>);
  const locationMarkers = useMapLocationMarkers(areaMap);
  const enrichedMarkers = useEnrichedMarkers(locationMarkers, areaMap.data);

  const hasData = areaMap.data !== undefined;
  const showMulti = totalMaps > 1;
  const downloadDisabled = isDownloadDisabled(areaMap);

  // Com um único mapa não faz sentido rotular "Mapa 1" — só há um.
  let cardTitle = '';
  if (hasData) cardTitle = buildMapTitle(areaMap);
  else if (showMulti) cardTitle = `Mapa ${position}`;

  const handleTap = (lat: number, lng: number, x: number, y: number) => {
    if (!areaMap.data?.length) return;
    const value = findNearestValue(lat, lng, areaMap.data);
    if (value === null) {
      setTapInfo(null);
      return;
    }
    setTapInfo({ x, y, value });
  };

  const alertNode = useMemo(() => (areaMap.error ? (
    <div className="agro-ci-area-map-error">
      <QueryErrorState
        title={areaMap.error.title}
        message={areaMap.error.message}
      />
    </div>
  ) : null), [areaMap.error]);

  return (
    <div className="agro-ci-area-mobile-card">
      <div className="agro-ci-area-mobile-card-header">
        <div className="agro-ci-area-mobile-card-heading">
          {showMulti && <span className="agro-ci-area-mobile-card-badge">{position}</span>}
          {cardTitle && (
            <span className="agro-ci-area-mobile-card-title">{cardTitle}</span>
          )}
        </div>
        <div className="agro-ci-area-mobile-card-actions">
          <button
            type="button"
            className="agro-ci-area-mobile-action agro-ci-area-mobile-action-layers"
            onClick={() => setLayersOpen(true)}
          >
            <Icon iconType="layers" color={cssVar('text-strong')} size="small" />
            Camadas
          </button>
          <button
            type="button"
            aria-label="Baixar mapa"
            className="agro-ci-area-mobile-action"
            onClick={() => setDownloadOpen(true)}
            disabled={downloadDisabled}
          >
            <Icon iconType="download" color={cssVar('text-strong')} size="small" />
          </button>
          {showMulti && (
            <button
              type="button"
              aria-label="Fechar mapa"
              className="agro-ci-area-mobile-action"
              onClick={() => removeMap(areaMap)}
              disabled={removeMapDisabled}
            >
              <Icon iconType="close" color={cssVar('text-strong')} size="small" />
            </button>
          )}
        </div>
      </div>

      <div className="agro-ci-area-map-wrapper" ref={divRef}>
        <InteractiveMap
          markers={layers.localidades ? enrichedMarkers : []}
          tileUrl={ESRI_SATELLITE_TILE_URL}
          tileAttribution={ESRI_SATELLITE_TILE_ATTRIBUTION}
        >
          <CanvasHeatLayer
            points={(!areaMap.isLoading && areaMap.data) || []}
            config={heatmapConfig}
            legend={legend}
          />
          {layers.limitesEstaduais ? <BrazilStatesLayer /> : <BrazilCountryLayer />}
          <InteractiveMap.MapUpdater observedDivRef={divRef} />
          <InteractiveMap.ImageDownloader
            setOnDownload={setOnDownloadClick}
            filename={areaMap.filename}
            caption={caption}
          />
          <InteractiveMap.MapTapHandler onTap={handleTap} />
        </InteractiveMap>

        {tapInfo && (
          <button
            type="button"
            aria-label="Fechar valor do ponto"
            className="agro-ci-area-map-tap-tip"
            style={{ left: tapInfo.x, top: tapInfo.y }}
            onClick={() => setTapInfo(null)}
          >
            <span className="agro-ci-area-map-tap-tip-label">Índice [adm]</span>
            <span className="agro-ci-area-map-tap-tip-value">{tapInfo.value.toFixed(2)}</span>
          </button>
        )}

        {alertNode}

        {areaMap.isLoading && (
          <div className="agro-map-loading">
            <Spinner animation="border" />
          </div>
        )}
      </div>

      {hasData && (
        <div className="agro-ci-area-map-hint">
          <Icon iconType="star" color={cssVar('text-accent')} size="small" />
          <span>Toque num pin para a análise por local, ou no mapa para ver o valor do ponto.</span>
        </div>
      )}

      <LayersSheet
        open={layersOpen}
        dismiss={() => setLayersOpen(false)}
        layers={layers}
        onToggle={handleToggle}
        definitions={LAYER_DEFS}
      />
      <DownloadSheet
        open={downloadOpen}
        dismiss={() => setDownloadOpen(false)}
        title={cardTitle || 'selecionado'}
        disabled={downloadDisabled}
        onConfirm={() => {
          onDownloadClick();
          setDownloadOpen(false);
        }}
      />
    </div>
  );
}
